import React, { useEffect, useRef, useState } from "react";
import API from "../../../services";
import { toast } from "react-toastify";
import "../AccountStatement/AccountStatement.scss";
import { useNavigate } from "react-router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { Loader } from "../../../constants/whitelabelEnv";
import { domain } from "../../../constants/Branding";
import { useTranslation } from "../../../hooks/useTranslation";
import { connect } from "react-redux";
import { RootState } from "../../../models/RootState";

interface SiteAccountStatementProps {
  userDetails?: any;
  dynamicTextColors?: any;
}

interface SiteProvider {
  name: string;
  tgProviderId: string;
  integratorId: string;
}

interface ProviderDropdownItem {
  item_id: string;
  item_text: string;
}

const DEFAULT_TG_PROVIDER_ID = "FS100";

const normalizeSiteProvider = (p: any): SiteProvider | null => {
  const name = String(p?.name ?? "").trim();
  const tgProviderId = String(p?.tgProviderId ?? "").trim();
  const integratorId = String(p?.integratorId ?? "").trim();
  if (!name || !tgProviderId || !integratorId) return null;
  return { name, tgProviderId, integratorId };
};

const displayCell = (value: any): string => {
  if (value === undefined || value === null) return "-";
  const s = String(value).trim();
  if (s === "" || s === "null" || s === "undefined") return "-";
  return s;
};

const SiteAccountStatement: React.FC<SiteAccountStatementProps> = ({ userDetails, dynamicTextColors }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<any[]>([]);
  const [pageSize] = useState(20);
  const [nextPageToken, setNextPageToken] = useState("");
  const [pageTokenHistory, setPageTokenHistory] = useState<string[]>([""]);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const [providers, setProviders] = useState<SiteProvider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState(DEFAULT_TG_PROVIDER_ID);
  const [providersLoading, setProvidersLoading] = useState(true);
  const providersFetchedOnceRef = useRef(false);

  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const groupProvidersForDropdown = (list: any[]): ProviderDropdownItem[] => {
    const validProviders = (Array.isArray(list) ? list : [])
      .map(normalizeSiteProvider)
      .filter(Boolean) as SiteProvider[];

    const groupedMap = new Map<string, { names: string[]; tgProviderIds: string[] }>();
    for (const p of validProviders) {
      const id = (p.integratorId || "").trim();
      const name = p.name.trim();
      const tgId = p.tgProviderId.trim();
      if (!id || !name || !tgId) continue;

      if (!groupedMap.has(id)) {
        groupedMap.set(id, { names: [name], tgProviderIds: [tgId] });
      } else {
        const group = groupedMap.get(id)!;
        if (!group.names.includes(name)) group.names.push(name);
        if (!group.tgProviderIds.includes(tgId)) group.tgProviderIds.push(tgId);
      }
    }

    return Array.from(groupedMap.values()).map((group) => ({
      item_id: group.tgProviderIds[0],
      item_text: group.names.join(" / "),
    }));
  };

  const providerDropdownItems = groupProvidersForDropdown(providers);

  const textColorClass = dynamicTextColors?.pageTextColorClass || "";
  const inputBoxTextColorClass = dynamicTextColors?.inputBoxTextColorClass || "";
  const isFirstRender = useRef(true);

  const dateFormat = (time: any) => {
    if (time === undefined || time === null || String(time).trim() === "") return "-";
    const matchStartTime = new Date(time);
    if (Number.isNaN(matchStartTime.getTime())) return "-";
    return matchStartTime.toLocaleString("en-GB", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    });
  };

  const fetchSiteProviders = async () => {
    try {
      const response = await API.post("/users/site-providers", {});
      if (response?.data?.status === "RS_OK" && Array.isArray(response.data.providers)) {
        const normalized = response.data.providers
          .map(normalizeSiteProvider)
          .filter(Boolean) as SiteProvider[];
        setProviders(normalized);

        const grouped = groupProvidersForDropdown(normalized);
        if (grouped.length > 0) {
          const hasSelected = grouped.some((g) => g.item_id === selectedProviderId);
          if (!hasSelected) setSelectedProviderId(grouped[0].item_id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch site providers:", err);
    } finally {
      setProvidersLoading(false);
    }
  };

  const getSiteAccountStatement = async (pageToken = "", providerId?: string) => {
    if (isFirstRender.current) {
      setLoading(true);
      isFirstRender.current = false;
    }
    setErrorMessage("");

    const tgProviderId = providerId ?? selectedProviderId;

    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    try {
      const response = await API.post(
        "/users/site-account-statement",
        {
          tgProviderId,
          startDate: String(start.getTime()),
          endDate: String(end.getTime()),
          pageSize,
          pageToken,
          sortOrder: "DESC",
          transactionType: "-1",
        }
      );

      if (response?.data?.status !== "RS_OK") {
        const message = response?.data?.errorDescription || "Failed to load site account statement.";
        toast.error(message);
        setErrorMessage(message);
        setLoading(false);
        return;
      }

      const rows = Array.isArray(response?.data?.accountStatement) ? response.data.accountStatement : [];
      setTickets(rows);
      setNextPageToken(response?.data?.pageToken || "");
    } catch (error: any) {
      const message = error?.response?.data?.errorDescription || "Failed to load site account statement.";
      toast.error(message);
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (providersFetchedOnceRef.current) return;
    providersFetchedOnceRef.current = true;
    fetchSiteProviders();
  }, []);

  useEffect(() => {
    if (!providersLoading) {
      setPageTokenHistory([""]);
      setNextPageToken("");
      isFirstRender.current = true;
      getSiteAccountStatement("", selectedProviderId);
    }
  }, [selectedProviderId, providersLoading]);

  const handleBackClick = () => {
    navigate(-1);
  };
  const handleNextPage = () => {
    if (!nextPageToken) return;
    setPageTokenHistory((prev) => [...prev, nextPageToken]);
    getSiteAccountStatement(nextPageToken);
  };

  const handlePrevPage = () => {
    if (pageTokenHistory.length <= 1) return;
    const updatedHistory = pageTokenHistory.slice(0, -1);
    const previousToken = updatedHistory[updatedHistory.length - 1] || "";
    setPageTokenHistory(updatedHistory);
    getSiteAccountStatement(previousToken);
  };

  const openDetails = (row: any) => {
    setSelectedRow(row);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedRow(null);
  };

  const pickValue = (row: any, key: string) => {
    const val = row?.[key];
    if (val === undefined || val === null) return "-";
    const s = String(val).trim();
    if (s === "" || s === "null" || s === "undefined") return "-";
    return val;
  };

  const toNumberOrNull = (value: any): number | null => {
    if (value === undefined || value === null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "boolean") return value ? 1 : 0;
    const n = Number(String(value).trim());
    return Number.isFinite(n) ? n : null;
  };

  const tableHeaderClass = `px-4 py-2 text-left border-b border-t whitespace-nowrap ${
    textColorClass === "dark-text" ? "text-black" : "text-white"
  }`;
  const tableHeaders = [
    "Transaction Time",
    "Transaction Type",
    "Amount",
    "Balance Before",
    "Balance After",
    "Event Name",
    "Market Type",
  ];

  return (
    <div className={`flex flex-col px-3 mt-4 w-full pb-[7rem] py-[4rem] AccStatement ${textColorClass}`}>
      {loading ? (
        <div className="home-loading">
          <img src={Loader(domain)} style={{ height: "10rem" }} alt="loading" />
        </div>
      ) : (
        <>
          {detailsOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className={`bg-inputbox-color p-6 rounded-lg w-full max-w-md relative ${textColorClass}`}>
                <CloseIcon
                  className={`absolute top-3 right-3 cursor-pointer ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}
                  onClick={closeDetails}
                />
                <h3 className={`text-lg mb-4 text-center ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                  Transaction Details
                </h3>

                {selectedRow ? (
                  <div className="space-y-3">
                    {[
                      {
                        label: "Transaction ID",
                        value: pickValue(selectedRow, "transactionId"),
                      },
                      {
                        label: "Bet ID",
                        value: pickValue(selectedRow, "betId"),
                      },
                      {
                        label: "Game Code",
                        value: pickValue(selectedRow, "gameCode"),
                      },
                      {
                        label: "Market ID",
                        value: pickValue(selectedRow, "marketId"),
                      },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between gap-3">
                        <div className={`text-sm ${textColorClass === "dark-text" ? "text-black" : "text-zinc-400"}`}>
                          {item.label}
                        </div>
                        <div className={`text-sm text-right break-all ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                          {displayCell(item.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          <header className={`flex gap-3 items-center self-start text-base font-medium text-center mb-[0.5rem] ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
            <div>
              <div className="bg-inputbox-color flex p-1 mr-[0.25rem] rounded" onClick={handleBackClick}>
                <ArrowBackIcon
                  className={`aspect-square w-[18px] ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                  style={{ width: "20px", height: "20px" }}
                />
              </div>
            </div>
            <h2 className="self-stretch my-auto">Site Account Statement</h2>
          </header>

          <div className="flex items-center gap-2 mb-3">
            <label
              className={`text-sm font-medium ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}
              htmlFor="provider-select"
            >
              Sites:
            </label>
            <select
              id="provider-select"
              className={`px-3 py-1.5 rounded bg-inputbox-color text-sm outline-none ${
                inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"
              }`}
              value={selectedProviderId}
              onChange={(e) => setSelectedProviderId(e.target.value)}
            >
              {providerDropdownItems.length > 0 ? (
                providerDropdownItems.map((p) => (
                  <option key={p.item_id} value={p.item_id}>
                    {p.item_text}
                  </option>
                ))
              ) : (
                <option value={DEFAULT_TG_PROVIDER_ID}>{DEFAULT_TG_PROVIDER_ID}</option>
              )}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse user-table border-x mt-1">
              <thead>
                <tr>
                  {tableHeaders.map((header) => (
                    <th key={header} className={tableHeaderClass}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tickets.map((row: any, index: number) => {
                  const amountValue = toNumberOrNull(row?.amount) ?? 0;

                  const balanceBefore =
                    toNumberOrNull(
                      row?.balanceBefore
                    ) ?? 0;

                  const balanceAfter =
                    toNumberOrNull(
                      row?.balanceAfter
                    ) ?? 0;
                  const eventName = displayCell(row?.eventName);
                  const marketType = displayCell(row?.marketType);
                  return (
                    <tr
                      key={index}
                      className="border-b cursor-pointer hover:bg-[#1f1f1f]"
                      onClick={() => openDetails(row)}
                    >
                      <td className={`px-2 py-2 whitespace-nowrap ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                        {dateFormat(
                          row?.transactionTime
                        )}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                        {displayCell(row?.transactionType)}
                      </td>
                      <td
                        className={`p-2 ${
                          amountValue > 0
                            ? "text-green-500"
                            : amountValue < 0
                              ? "text-red-500"
                              : textColorClass === "dark-text" ? "text-black" : "text-white"
                        }`}
                      >
                        {amountValue % 1 === 0 ? amountValue : amountValue.toFixed(2)}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                        {balanceBefore % 1 === 0 ? balanceBefore : Number(balanceBefore).toFixed(2)}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                        {balanceAfter % 1 === 0 ? balanceAfter : balanceAfter.toFixed(2)}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>{eventName}</td>
                      <td className={`px-2 py-2 whitespace-nowrap ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>{marketType}</td>
                    </tr>
                  );
                })}
                {errorMessage ? (
                  <tr>
                    <td colSpan={7} className={`px-4 py-4 text-center text-red-400`}>
                      {errorMessage}
                    </td>
                  </tr>
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={`px-4 py-4 text-center ${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
                      No site account statement found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <button
              className={`px-3 py-1 rounded bg-inputbox-color ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"} disabled:opacity-50`}
              onClick={handlePrevPage}
              disabled={loading || pageTokenHistory.length <= 1}
            >
              Previous
            </button>
            <span className={`${textColorClass === "dark-text" ? "text-black" : "text-white"}`}>
              {tickets.length} items
            </span>
            <button
              className={`px-3 py-1 rounded bg-inputbox-color ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"} disabled:opacity-50`}
              onClick={handleNextPage}
              disabled={loading || !nextPageToken}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userDetails: state.userDetails?.userDetails,
    dynamicTextColors: state.userDetails?.dynamicTextColors,
  };
};

export default connect(mapStateToProps)(SiteAccountStatement);
